/**
 * Typed API client for the booking tool (Task 12).
 *
 * Two groups of calls:
 *  - STAFF  /api/booking/*         — authenticated; reuses `fetchWithAuth`
 *    from api.ts (Supabase session token in the Authorization header).
 *  - PUBLIC /api/public/booking/*  — NO auth header; must work for anonymous
 *    invitees. Hold/confirm/reschedule return 409 {reason:"taken"} and
 *    410 {reason:"expired"|...} responses as typed results (not thrown
 *    errors) so the UI can branch on them; all other non-2xx throw with the
 *    server's error message.
 *
 * Shapes mirror server/routes/booking.ts exactly — staff endpoints return
 * raw snake_case rows; public endpoints return hand-projected camelCase
 * objects.
 */

import { fetchWithAuth } from "./api";

// ---------------------------------------------------------------------------
// Staff-side row types (snake_case — raw PostgREST rows from the server)
// ---------------------------------------------------------------------------

export type LocationType = "zoom" | "phone" | "in_person";
export type QuestionFieldType = "text" | "textarea" | "phone" | "select";

export interface BookingPage {
  id: string;
  user_id: string;
  slug: string;
  display_name: string;
  title: string | null;
  photo_url: string | null;
  timezone: string;
  is_active: boolean;
}

export interface EventType {
  id: string;
  booking_page_id: string;
  slug: string;
  name: string;
  description: string | null;
  duration_min: number;
  buffer_before_min: number;
  buffer_after_min: number;
  min_notice_min: number;
  max_advance_days: number;
  daily_cap: number | null;
  location_type: LocationType;
  location_detail: string | null;
  is_active: boolean;
  list_index: number;
}

export interface AvailabilityRule {
  id: string;
  booking_page_id: string;
  weekday: number; // 0–6
  start_time: string; // PostgREST returns "HH:MM:SS"
  end_time: string;
}

export interface DateOverride {
  id: string;
  booking_page_id: string;
  date: string; // "YYYY-MM-DD"
  is_unavailable: boolean;
  start_time: string | null;
  end_time: string | null;
}

export interface EventTypeQuestion {
  id: string;
  event_type_id: string;
  label: string;
  field_type: QuestionFieldType;
  required: boolean;
  options: string[] | null;
  list_index: number;
}

// ---------------------------------------------------------------------------
// Staff-side request/response payloads
// ---------------------------------------------------------------------------

export interface MyBookingPage {
  page: BookingPage;
  eventTypes: EventType[];
  availabilityRules: AvailabilityRule[];
  dateOverrides: DateOverride[];
  googleConnected: boolean;
}

export interface BookingPageUpdate {
  display_name?: string;
  title?: string | null;
  photo_url?: string | null;
  timezone?: string;
  slug?: string;
  is_active?: boolean;
}

/** Create requires name/slug/duration_min/location_type; the rest default server-side. */
export interface EventTypeCreate {
  name: string;
  slug: string;
  duration_min: number;
  location_type: LocationType;
  description?: string | null;
  buffer_before_min?: number;
  buffer_after_min?: number;
  min_notice_min?: number;
  max_advance_days?: number;
  daily_cap?: number | null;
  location_detail?: string | null;
  is_active?: boolean;
  list_index?: number;
}

export type EventTypeUpdate = Partial<EventTypeCreate>;

export interface AvailabilityRuleInput {
  weekday: number; // 0–6
  start_time: string; // "HH:MM"
  end_time: string;
}

export interface DateOverrideInput {
  date: string; // "YYYY-MM-DD"
  is_unavailable?: boolean; // default true
  start_time?: string; // required (HH:MM) when is_unavailable is false
  end_time?: string;
}

export interface EventTypeQuestionInput {
  label: string;
  field_type: QuestionFieldType;
  required?: boolean;
  options?: string[]; // required for "select"
  list_index?: number;
}

// ---------------------------------------------------------------------------
// Staff endpoints (auth header via fetchWithAuth)
// ---------------------------------------------------------------------------

/** GET /api/booking/my-page — auto-creates the page on first call. */
export const getMyBookingPage = (): Promise<MyBookingPage> =>
  fetchWithAuth<MyBookingPage>("/api/booking/my-page");

/** PUT /api/booking/my-page */
export const updateMyBookingPage = (update: BookingPageUpdate): Promise<{ page: BookingPage | null }> =>
  fetchWithAuth<{ page: BookingPage | null }>("/api/booking/my-page", {
    method: "PUT",
    body: JSON.stringify(update),
  });

/** POST /api/booking/event-types */
export const createEventType = (input: EventTypeCreate): Promise<{ eventType: EventType | null }> =>
  fetchWithAuth<{ eventType: EventType | null }>("/api/booking/event-types", {
    method: "POST",
    body: JSON.stringify(input),
  });

/** PUT /api/booking/event-types/:id */
export const updateEventType = (
  id: string,
  update: EventTypeUpdate
): Promise<{ eventType: EventType | null }> =>
  fetchWithAuth<{ eventType: EventType | null }>(`/api/booking/event-types/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(update),
  });

/** DELETE /api/booking/event-types/:id — 500s if the event type has bookings (ON DELETE RESTRICT). */
export const deleteEventType = (id: string): Promise<{ ok: boolean }> =>
  fetchWithAuth<{ ok: boolean }>(`/api/booking/event-types/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });

/** POST /api/booking/availability-rules — replace-all semantics. */
export const replaceAvailabilityRules = (
  rules: AvailabilityRuleInput[]
): Promise<{ availabilityRules: AvailabilityRule[] }> =>
  fetchWithAuth<{ availabilityRules: AvailabilityRule[] }>("/api/booking/availability-rules", {
    method: "POST",
    body: JSON.stringify({ rules }),
  });

/** POST /api/booking/date-overrides */
export const createDateOverride = (
  input: DateOverrideInput
): Promise<{ dateOverride: DateOverride | null }> =>
  fetchWithAuth<{ dateOverride: DateOverride | null }>("/api/booking/date-overrides", {
    method: "POST",
    body: JSON.stringify(input),
  });

/** DELETE /api/booking/date-overrides/:id */
export const deleteDateOverride = (id: string): Promise<{ ok: boolean }> =>
  fetchWithAuth<{ ok: boolean }>(`/api/booking/date-overrides/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });

/** GET /api/booking/event-types/:id/questions — intake questions for one of your own event types. */
export const getEventTypeQuestions = (
  eventTypeId: string
): Promise<{ questions: EventTypeQuestion[] }> =>
  fetchWithAuth<{ questions: EventTypeQuestion[] }>(
    `/api/booking/event-types/${encodeURIComponent(eventTypeId)}/questions`
  );

/** POST /api/booking/event-types/:id/questions — replace-all semantics. */
export const replaceEventTypeQuestions = (
  eventTypeId: string,
  questions: EventTypeQuestionInput[]
): Promise<{ questions: EventTypeQuestion[] }> =>
  fetchWithAuth<{ questions: EventTypeQuestion[] }>(
    `/api/booking/event-types/${encodeURIComponent(eventTypeId)}/questions`,
    { method: "POST", body: JSON.stringify({ questions }) }
  );

/** GET /api/booking/google/connect — returns the Google consent URL to navigate to. */
export const getGoogleConnectUrl = (): Promise<{ url: string }> =>
  fetchWithAuth<{ url: string }>("/api/booking/google/connect");

/** DELETE /api/booking/google */
export const disconnectGoogle = (): Promise<{ ok: boolean }> =>
  fetchWithAuth<{ ok: boolean }>("/api/booking/google", { method: "DELETE" });

// ---------------------------------------------------------------------------
// Public-side types (camelCase — hand-projected by the server, public-safe)
// ---------------------------------------------------------------------------

export interface PublicQuestion {
  label: string;
  fieldType: QuestionFieldType;
  required: boolean;
  options: string[] | null;
  listIndex: number;
}

export interface PublicEventType {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  durationMin: number;
  locationType: LocationType;
  /** Only present when locationType === "in_person". */
  locationDetail?: string | null;
  questions: PublicQuestion[];
}

export interface PublicBookingPage {
  page: {
    slug: string;
    displayName: string;
    title: string | null;
    photoUrl: string | null;
    timezone: string;
  };
  eventTypes: PublicEventType[];
}

/** A computed open slot, both ends UTC ISO timestamps. */
export interface Slot {
  start: string;
  end: string;
}

export interface ManageBooking {
  status: string; // "confirmed" | "cancelled" | ...
  start: string; // UTC ISO
  end: string;
  inviteeName: string;
  inviteeEmail: string;
  inviteeTimezone: string;
  eventType: {
    slug: string;
    name: string;
    durationMin: number;
    locationType: LocationType;
  };
  staffName: string;
  pageSlug: string;
}

export interface ConfirmBookingInput {
  name: string;
  email: string;
  timezone: string; // IANA zone
  answers?: Record<string, string>;
}

/** Result of placing a hold. `taken` = slot no longer available (HTTP 409). */
export type HoldResult =
  | { ok: true; holdId: string; holdExpiresAt: string }
  | { ok: false; reason: "taken" };

/** Result of confirming a hold. `expired` = hold expired or unknown (HTTP 410). */
export type ConfirmResult =
  | { ok: true; manageToken: string; joinUrl?: string }
  | { ok: false; reason: "expired" };

/**
 * Result of a reschedule. 409 → "taken"; 410 → "not_active" (booking is
 * cancelled or still a hold) or "invalid_range".
 */
export type RescheduleResult =
  | { ok: true; manageToken: string }
  | { ok: false; reason: "taken" | "not_active" | "invalid_range" | string };

// ---------------------------------------------------------------------------
// Public fetch helpers — NO auth header (must work for anonymous invitees)
// ---------------------------------------------------------------------------

const publicFetch = async <T,>(url: string, options?: RequestInit): Promise<T> => {
  const response = await fetch(url, {
    ...options,
    headers: { "Content-Type": "application/json", ...options?.headers },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Request failed: ${response.status}`);
  }
  return response.json();
};

/**
 * Like publicFetch, but 409/410 responses (the server's branchable
 * {reason} results) resolve to `{ ok: false, reason }` instead of throwing.
 */
const publicFetchBranchable = async <T,>(
  url: string,
  options: RequestInit,
  fallbackReason: string
): Promise<{ ok: true; data: T } | { ok: false; reason: string }> => {
  const response = await fetch(url, {
    ...options,
    headers: { "Content-Type": "application/json", ...options?.headers },
  });
  if (response.status === 409 || response.status === 410) {
    const body = await response.json().catch(() => ({}));
    return { ok: false, reason: typeof body.reason === "string" ? body.reason : fallbackReason };
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Request failed: ${response.status}`);
  }
  return { ok: true, data: (await response.json()) as T };
};

// ---------------------------------------------------------------------------
// Public endpoints
// ---------------------------------------------------------------------------

/** GET /api/public/booking/:slug — page + active event types + intake questions. */
export const getPublicBookingPage = (slug: string): Promise<PublicBookingPage> =>
  publicFetch<PublicBookingPage>(`/api/public/booking/${encodeURIComponent(slug)}`);

/** GET /api/public/booking/:slug/:eventTypeSlug/slots?from&to — max 62-day range. */
export const getPublicSlots = (
  slug: string,
  eventTypeSlug: string,
  fromIso: string,
  toIso: string
): Promise<{ slots: Slot[] }> =>
  publicFetch<{ slots: Slot[] }>(
    `/api/public/booking/${encodeURIComponent(slug)}/${encodeURIComponent(eventTypeSlug)}/slots` +
      `?from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`
  );

/** POST /api/public/booking/:slug/:eventTypeSlug/hold — 7-minute hold on a slot. */
export const holdSlot = async (
  slug: string,
  eventTypeSlug: string,
  startIso: string,
  endIso: string,
  inviteeTimezone?: string
): Promise<HoldResult> => {
  const result = await publicFetchBranchable<{ holdId: string; holdExpiresAt: string }>(
    `/api/public/booking/${encodeURIComponent(slug)}/${encodeURIComponent(eventTypeSlug)}/hold`,
    {
      method: "POST",
      body: JSON.stringify({ start: startIso, end: endIso, ...(inviteeTimezone ? { timezone: inviteeTimezone } : {}) }),
    },
    "taken"
  );
  if (!result.ok) return { ok: false, reason: "taken" };
  return { ok: true, holdId: result.data.holdId, holdExpiresAt: result.data.holdExpiresAt };
};

/** POST /api/public/booking/hold/:holdId/confirm */
export const confirmHold = async (
  holdId: string,
  input: ConfirmBookingInput
): Promise<ConfirmResult> => {
  const result = await publicFetchBranchable<{ manageToken: string; joinUrl?: string }>(
    `/api/public/booking/hold/${encodeURIComponent(holdId)}/confirm`,
    { method: "POST", body: JSON.stringify(input) },
    "expired"
  );
  if (!result.ok) return { ok: false, reason: "expired" };
  return {
    ok: true,
    manageToken: result.data.manageToken,
    ...(result.data.joinUrl ? { joinUrl: result.data.joinUrl } : {}),
  };
};

/** GET /api/public/booking/manage/:token — booking summary for the manage page. */
export const getManagedBooking = (manageToken: string): Promise<ManageBooking> =>
  publicFetch<ManageBooking>(`/api/public/booking/manage/${encodeURIComponent(manageToken)}`);

/** POST /api/public/booking/manage/:token/reschedule — returns a NEW manage token on success. */
export const rescheduleBooking = async (
  manageToken: string,
  startIso: string,
  endIso: string
): Promise<RescheduleResult> => {
  const result = await publicFetchBranchable<{ manageToken: string }>(
    `/api/public/booking/manage/${encodeURIComponent(manageToken)}/reschedule`,
    { method: "POST", body: JSON.stringify({ start: startIso, end: endIso }) },
    "taken"
  );
  if (!result.ok) return { ok: false, reason: result.reason };
  return { ok: true, manageToken: result.data.manageToken };
};

/** POST /api/public/booking/manage/:token/cancel — throws on 404/410/500. */
export const cancelBooking = (manageToken: string): Promise<{ ok: boolean }> =>
  publicFetch<{ ok: boolean }>(
    `/api/public/booking/manage/${encodeURIComponent(manageToken)}/cancel`,
    { method: "POST", body: JSON.stringify({}) }
  );
